import { useEffect, useRef, useState, useMemo, type FormEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { StatusCard } from '@/components/ui/card';
import { Callout } from '@/components/ui/callout';
import { SwitchField } from '@/components/ui/switch';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { ToolSummary } from '../api/TraceabilityResponseType';
import { useToolSummaries } from '../hooks/useToolSummaries';
import { toolWriteErrorMessage } from '../utils/toolForm';
import { ExistingToolIds } from './pickers/ExistingToolIds';
import { takenToolId } from './pickers/pickerHelpers';
import { ReferencePicker } from './pickers/ReferencePicker';
import { CompanyField } from './CompanyField';
import { KeyenceCameraApiClient } from '../api/KeyenceCameraApiClient';
import { AdvancedSettings } from './AdvancedSettings';
import { WorkshopSessionLog, type SessionEntry } from './WorkshopSessionLog';
import { cn } from '@/lib/utils';
import { Scan, Wrench } from 'lucide-react';
import { AdminSectionCard } from '@/features/admin/components/AdminSectionCard';

type ScanPhase = 'idle' | 'connecting' | 'scanning' | 'detected';

type WorkshopToolFormProps = {
  companyName: string;
  companyAutoFilled: boolean;
};

export const WorkshopToolForm: React.FC<WorkshopToolFormProps> = ({
  companyName,
  companyAutoFilled,
}) => {
  const http = useApiClient();
  const { t } = useTranslation();
  const { summaries: loadedSummaries, refresh: refreshSummaries } = useToolSummaries();

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const [backendUrl, setBackendUrl] = useState('https://localhost:44312');
  const [cameraIp, setCameraIp] = useState('192.168.100.100');
  const [isConnected, setIsConnected] = useState(false);
  const [scanPhase, setScanPhase] = useState<ScanPhase>('idle');
  const [scanError, setScanError] = useState<string | null>(null);
  const [justRegistered, setJustRegistered] = useState(false);
  const [autoRegister, setAutoRegister] = useState(false);
  const [registeringId, setRegisteringId] = useState<string | null>(null);

  const [reference, setReference] = useState('');
  const [company, setCompany] = useState(companyName);
  const [toolId, setToolId] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    ok: boolean;
    registeredId?: string;
    message?: string;
  } | null>(null);
  const submitControllerRef = useRef<AbortController | null>(null);

  const [sessionLog, setSessionLog] = useState<SessionEntry[]>([]);

  const apiClient = useMemo(() => new KeyenceCameraApiClient(backendUrl), [backendUrl]);

  // Tools registered during this session count as existing even before the list reloads, so a
  // tool scanned twice in a series is refused the second time.
  const summaries = useMemo<ToolSummary[] | null>(() => {
    if (!loadedSummaries) return null;
    const known = new Set(loadedSummaries.map((summary) => summary.toolId));
    const registered = sessionLog
      .filter((entry) => entry.ok && !known.has(entry.toolId))
      .map((entry) => ({
        toolId: entry.toolId,
        reference: entry.reference,
        traceCount: 0,
        lastTraceAt: null,
      }));
    return [...loadedSummaries, ...registered];
  }, [loadedSummaries, sessionLog]);
  const taken = takenToolId(toolId, summaries);

  useEffect(() => {
    if (companyAutoFilled) setCompany(companyName);
  }, [companyName, companyAutoFilled]);

  useEffect(() => () => { submitControllerRef.current?.abort(); }, []);

  async function handleScanClick() {
    setScanError(null);
    setSubmitResult(null);
    setJustRegistered(false);

    if (!isConnected) {
      setScanPhase('connecting');
      try {
        const res = await apiClient.connect(cameraIp);
        if (!res.Success || !res.Data?.IsConnected) {
          setScanPhase('idle');
          setScanError(res.Message ?? t('traceability.scanPage.keyence.errors.connectionFailed'));
          return;
        }
        setIsConnected(true);
      } catch (err) {
        setScanPhase('idle');
        setScanError(err instanceof Error ? err.message : t('traceability.scanPage.keyence.errors.networkError'));
        return;
      }
    }

    setScanPhase('scanning');
    try {
      const res = await apiClient.scan(10000);
      if (res.Success && res.Data?.Success) {
        const detected = (res.Data.Result ?? '').replace(/\r/g, '').trim();
        setToolId(detected);
        setScanPhase('detected');
        try { await apiClient.disconnect(); setIsConnected(false); } catch { /* non-fatal */ }
        if (autoRegister) {
          await registerTool(detected);
        }
      } else {
        setScanPhase('idle');
        setScanError(res.Data?.ErrorMessage ?? res.Message ?? t('traceability.scanPage.keyence.errors.scanFailed'));
      }
    } catch (err) {
      setScanPhase('idle');
      setScanError(err instanceof Error ? err.message : t('traceability.scanPage.keyence.errors.networkError'));
    }
  }

  async function registerTool(registeredId: string) {
    if (!traceabilityApiClient) return;
    const toolReference = reference.trim();

    submitControllerRef.current?.abort();
    const ac = new AbortController();
    submitControllerRef.current = ac;

    setSubmitResult(null);
    setIsSubmitting(true);
    setRegisteringId(registeredId);

    const now = new Date();
    const timestamp = [now.getHours(), now.getMinutes(), now.getSeconds()]
      .map((n) => String(n).padStart(2, '0'))
      .join(':');

    // An existing ID is refused, not overwritten (its reference is changed on the tool page).
    if (takenToolId(registeredId, summaries)) {
      const message = t('toolPickers.existingIds.takenShort', { toolId: registeredId });
      setSessionLog((prev) => [{ toolId: registeredId, reference: toolReference, ok: false, error: message, timestamp }, ...prev]);
      setSubmitResult({ ok: false, message });
      setIsSubmitting(false);
      setRegisteringId(null);
      return;
    }

    try {
      const res = await traceabilityApiClient.toolwrite(
        { tool: { toolId: registeredId, reference: toolReference, companyName: company, traces: [] } },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      setSessionLog((prev) => [{
        toolId: registeredId, reference: toolReference, ok: res.ok, timestamp,
        error: res.ok ? undefined : toolWriteErrorMessage(res, t, 'traceability.workshop.toolForm.errors.default'),
      }, ...prev]);

      if (res.ok) {
        refreshSummaries();
        setToolId('');
        setScanPhase('idle');
        if (autoRegister) {
          await handleScanClick();
        } else {
          setJustRegistered(true);
          setSubmitResult({ ok: true, registeredId });
        }
      } else {
        setSubmitResult({ ok: false, message: toolWriteErrorMessage(res, t, 'traceability.workshop.toolForm.errors.default') });
      }
    } catch {
      if (ac.signal.aborted) return;
      const message = t('traceability.workshop.toolForm.errors.default');
      setSessionLog((prev) => [{ toolId: registeredId, reference: toolReference, ok: false, error: message, timestamp }, ...prev]);
      setSubmitResult({ ok: false, message });
    } finally {
      if (!ac.signal.aborted) { setIsSubmitting(false); setRegisteringId(null); }
    }
  }

  async function handleRescan() {
    setToolId('');
    await handleScanClick();
  }

  async function handleCancelScan() {
    try { await apiClient.cancelScan(5000); } catch { /* ignore */ }
    setScanPhase('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await registerTool(toolId.trim());
  }

  const isBusy = scanPhase === 'connecting' || scanPhase === 'scanning' || isSubmitting;
  const isAutoRegistering = isSubmitting && autoRegister;

  const statusLabel = scanPhase === 'detected'
    ? t('traceability.workshop.toolForm.scan.statusDetected')
    : justRegistered
      ? t('traceability.workshop.toolForm.scan.statusReady')
      : isConnected
        ? t('traceability.workshop.toolForm.scan.statusConnected', { ip: cameraIp })
        : t('traceability.workshop.toolForm.scan.statusDisconnected');

  const dotActive = scanPhase === 'detected' || isConnected || justRegistered;

  return (
    <div className="flex flex-col gap-6">
      <AdminSectionCard icon={Wrench} title={t('traceability.workshop.toolForm.single.sectionTitle')}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">

          <div className="grid items-start gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="wf-reference">{t('traceability.workshop.toolForm.fields.reference.label')}</Label>
              <ReferencePicker id="wf-reference" value={reference} onChange={setReference}
                summaries={summaries}
                placeholder={t('traceability.workshop.toolForm.fields.reference.placeholder')} required />
            </div>
            <CompanyField id="wf-company" value={company} onChange={setCompany}
              autoFilled={companyAutoFilled} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="wf-toolid">{t('traceability.workshop.toolForm.fields.toolId.label')}</Label>
            <div className="flex gap-2">
              <Input id="wf-toolid" type="text" value={toolId}
                onChange={(e) => { setToolId(e.target.value); if (scanPhase === 'detected') setScanPhase('idle'); }}
                placeholder={scanPhase === 'scanning'
                  ? t('traceability.workshop.toolForm.scan.scanning')
                  : t('traceability.workshop.toolForm.fields.toolId.placeholder')}
                disabled={scanPhase === 'connecting' || scanPhase === 'scanning' || isAutoRegistering}
                required
                aria-invalid={taken ? true : undefined}
                aria-describedby="wf-toolid-existing"
                className={cn('font-mono', scanPhase === 'detected' && 'border-primary bg-primary/10', taken && 'border-destructive bg-transparent')} />
              <Button type="button"
                onClick={scanPhase === 'detected' ? handleRescan : handleScanClick}
                loading={isAutoRegistering || scanPhase === 'connecting' || scanPhase === 'scanning'}
                disabled={isBusy}
                variant={scanPhase === 'detected' ? 'secondary' : 'default'}
                className="shrink-0">
                {isAutoRegistering ? (
                  t('traceability.workshop.toolForm.autoRegister.registering')
                ) : scanPhase === 'connecting' ? (
                  t('traceability.workshop.toolForm.scan.connecting')
                ) : scanPhase === 'scanning' ? (
                  t('traceability.workshop.toolForm.scan.scanning')
                ) : scanPhase === 'detected' ? (
                  <><Scan aria-hidden />{t('traceability.workshop.toolForm.scan.buttonRescan')}</>
                ) : justRegistered ? (
                  <><Scan aria-hidden />{t('traceability.workshop.toolForm.scan.buttonNext')}</>
                ) : (
                  <><Scan aria-hidden />{t('traceability.workshop.toolForm.scan.button')}</>
                )}
              </Button>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full',
                dotActive ? 'bg-primary' : 'bg-muted-foreground/40')} />
              {statusLabel}
            </div>
            <ExistingToolIds id="wf-toolid-existing" value={toolId} summaries={summaries}
              onUse={(id) => { setToolId(id); if (scanPhase === 'detected') setScanPhase('idle'); }} />
          </div>

          {/* Auto-register toggle */}
          <SwitchField
            checked={autoRegister}
            onCheckedChange={setAutoRegister}
            disabled={isBusy}
            label={t('traceability.workshop.toolForm.autoRegister.label')}
            description={t('traceability.workshop.toolForm.autoRegister.description')}
          />

          <AdvancedSettings backendUrl={backendUrl} cameraIp={cameraIp}
            onBackendUrlChange={setBackendUrl} onCameraIpChange={setCameraIp} disabled={isBusy} />

          {scanPhase === 'scanning' && (
            <Callout
              tone="info"
              loading
              action={
                <Button type="button" variant="outline" size="sm" onClick={handleCancelScan}>
                  {t('traceability.workshop.toolForm.scan.cancel')}
                </Button>
              }
            >
              {t('traceability.workshop.toolForm.scan.waitingOverlay')}
            </Callout>
          )}

          {isAutoRegistering && registeringId && (
            <Callout tone="info" loading>
              {t('traceability.workshop.toolForm.autoRegister.registeringFlash', { toolId: registeringId })}
            </Callout>
          )}

          {scanError && <Callout tone="error">{scanError}</Callout>}

          {submitResult?.ok && (
            <Callout tone="success">
              {t('traceability.workshop.toolForm.scan.successFlash', { toolId: submitResult.registeredId ?? '' })}
            </Callout>
          )}
          {submitResult && !submitResult.ok && (
            <StatusCard type="error" message={submitResult.message ?? t('traceability.workshop.toolForm.errors.default')} />
          )}

          <Button
            type="submit"
            className="w-full"
            loading={isSubmitting && !isAutoRegistering}
            disabled={isBusy || taken !== null}
          >
            {t('traceability.workshop.toolForm.submit.single')}
          </Button>
        </form>
      </AdminSectionCard>

      <WorkshopSessionLog entries={sessionLog} onClear={() => setSessionLog([])} />
    </div>
  );
};
